import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useTableState from '../../hooks/useTableState';
import { Box, IconButton, Tooltip } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { DownloadIcon } from '@phosphor-icons/react';
import { download, generateCsv, mkConfig } from 'export-to-csv';
import { getAllCustomers } from '../../api/customerApi';
import { getAllSales, getAllSalesByAgentId } from '../../api/salesApi';
import { useUser } from '../../context/userContext';
import handleAgentName from '../../utils/userUtils';
import { formatPhoneNumber, openWhatsApp } from '../../utils/phoneUtils';
import { handlePriceWithRM } from '../../utils/priceUtils';
import { convertDateTimeToDateString } from '../../utils/datetimeUtils';
import { icBirthday } from '../../utils/icBirthday';
import { facetFilterOptions } from '../common/facetFilter';

/**
 * All Customer.
 *
 * The columns are the ones asked for: who they are, how to reach them, what they
 * bought and when, and the birthday. (user 2026-10-21: "customer name, phone,
 * whatsapp button at the end to whatsapp ccustomer with their phone number. kwp,
 * final price, deposit date, birthday")
 *
 * Email and address are no longer columns — they are on the customer's own page,
 * and carrying them here pushed the numbers off a laptop screen.
 *
 * A customer with more than one sale is still ONE row: kWp and price are the
 * totals across their sales, the deposit date is the most recent one, and the
 * count sits next to the name so a total is never mistaken for a single sale.
 */
export default function CustomerList() {
    const ts = useTableState();
    const { user } = useUser();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);

    const isAdmin = user?.roleRights?.includes('AllSalesList');

    // Stay inside the module the person is already in — /accounting/customers for
    // the accountant, /sales/customers for an agent — so the breadcrumb and the
    // sidebar highlight do not jump on the way to a customer.
    const base = pathname.startsWith('/accounting') ? '/accounting/customers' : '/sales/customers';

    const fetchData = useCallback(async () => {
        try {
            const salesRes = isAdmin
                ? await getAllSales()
                : await getAllSalesByAgentId(user.userId);

            const salesData = salesRes.data;

            // Keyed on the customer id, which the sales list now actually carries.
            // It used to key on the NAME, which merged two different people who
            // happen to share one — and there are plenty of those.
            const customerMap = {};
            salesData.forEach(sale => {
                const key = sale.customerId || `name:${sale.customerDetails?.customerName ?? ''}`;
                if (!customerMap[key]) {
                    customerMap[key] = {
                        customerId: sale.customerId || null,
                        customerName: sale.customerDetails?.customerName ?? '',
                        email: sale.customerDetails?.email ?? '',
                        phoneNumber: sale.customerDetails?.phoneNumber ?? '',
                        address: sale.customerDetails?.address ?? '',
                        ic: sale.customerDetails?.ic ?? '',
                        salesAgent: sale.agentDetails ? `${sale.agentDetails.groupName ?? ''} - ${handleAgentName(sale.agentDetails.firstName, sale.agentDetails.lastName, sale.agentDetails.alias)}`.trim() : '',
                        coAgent: sale.coAgentDetails ? `${sale.coAgentDetails.groupName ?? ''} - ${handleAgentName(sale.coAgentDetails.firstName, sale.coAgentDetails.lastName, sale.coAgentDetails.alias)}`.trim() : '',
                        salesCount: 0,
                        energy: 0,
                        finalPrice: 0,
                        depositOn: null,
                    };
                }
                const row = customerMap[key];
                row.salesCount += 1;
                row.energy += Number(sale.energy) || 0;
                row.finalPrice += Number(sale.finalPrice) || 0;
                // Most recent deposit wins; a sale with no deposit date never displaces one.
                if (sale.depositOn && (!row.depositOn || new Date(sale.depositOn) > new Date(row.depositOn))) {
                    row.depositOn = sale.depositOn;
                }
                // An IC on any of their sales is an IC for the customer.
                if (!row.ic && sale.customerDetails?.ic) row.ic = sale.customerDetails.ic;
                // Likewise a co-broke: if their first sale had none and a later one did,
                // the column should say so rather than stay blank.
                if (!row.coAgent && sale.coAgentDetails) {
                    row.coAgent = `${sale.coAgentDetails.groupName ?? ''} - ${handleAgentName(sale.coAgentDetails.firstName, sale.coAgentDetails.lastName, sale.coAgentDetails.alias)}`.trim();
                }
            });

            // Customers with no sales at all exist only in the customer table.
            if (isAdmin) {
                try {
                    const custRes = await getAllCustomers();
                    custRes.data.forEach(c => {
                        const existing = customerMap[c.customerId];
                        if (!existing) {
                            customerMap[c.customerId] = {
                                customerId: c.customerId,
                                customerName: c.customerName ?? '',
                                email: c.email ?? '',
                                phoneNumber: c.phoneNumber ?? '',
                                address: c.address ?? '',
                                ic: c.ic ?? '',
                                salesAgent: '',
                                coAgent: '',
                                salesCount: 0,
                                energy: 0,
                                finalPrice: 0,
                                depositOn: null,
                            };
                        } else {
                            // The customer table is the better record for contact details.
                            existing.email = c.email || existing.email;
                            existing.phoneNumber = c.phoneNumber || existing.phoneNumber;
                            existing.address = c.address || existing.address;
                            existing.ic = c.ic || existing.ic;
                        }
                    });
                } catch (e) { /* the list still stands on the sales data alone */ }
            }

            setCustomers(Object.values(customerMap).map(c => ({
                ...c,
                birthday: icBirthday(c.ic),
            })));
        } catch (error) {
            console.error('Error fetching customers:', error);
        } finally {
            setLoading(false);
        }
    }, [isAdmin, user?.userId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const openCustomer = (row) => {
        if (!row.customerId) return;   // a name-only row has no page to open
        navigate(`${base}/${row.customerId}`);
    };

    const columns = useMemo(() => [
        {
            id: 'customerName', header: 'Customer Name', accessorKey: 'customerName', size: 190,
            Cell: ({ row }) => (
                <Box component="span">
                    {row.original.customerName || '—'}
                    {row.original.salesCount > 1 && (
                        <Box component="span" sx={{ color: '#94A3B8', fontSize: '0.72rem', ml: 0.75 }}>
                            · {row.original.salesCount} sales
                        </Box>
                    )}
                </Box>
            ),
        },
        { id: 'phoneNumber', header: 'Phone', accessorFn: row => formatPhoneNumber(row.phoneNumber) || '—', size: 130 },
        {
            // Read out of the IC, never typed. Blank where the IC is missing or is
            // not a real date — a wrong birthday is worse than no birthday.
            id: 'birthday', header: 'Birthday', accessorFn: row => row.birthday ?? '', size: 115,
            Cell: ({ cell }) => cell.getValue() || '—',
        },
        {
            id: 'energy', header: 'kWp', accessorFn: row => row.energy || 0, size: 90,
            Cell: ({ cell }) => (cell.getValue() ? Number(cell.getValue()).toFixed(2) : '—'),
        },
        {
            id: 'finalPrice', header: 'Final Price', accessorFn: row => row.finalPrice || 0, size: 125,
            Cell: ({ cell }) => (cell.getValue() ? handlePriceWithRM(cell.getValue()) : '—'),
        },
        {
            id: 'depositOn', header: 'Deposit Date', accessorFn: row => row.depositOn ?? '', size: 125,
            Cell: ({ cell }) => (cell.getValue() ? convertDateTimeToDateString(cell.getValue()) : '—'),
        },
        { id: 'salesAgent', header: 'Sales Agent', accessorFn: row => row.salesAgent || '—', size: 150 },
        // Co-broke stays on the table — a customer the team shares is the case where
        // "whose customer is this" cannot be answered by the agent column alone.
        // (user 2026-10-21: "need to add cobroke aso on table")
        { id: 'coAgent', header: 'Co-Agent (Offline)', accessorFn: row => row.coAgent || '—', size: 150 },
        {
            id: 'actions', header: '', size: 110, enableSorting: false, enableColumnFilter: false,
            Cell: ({ row }) => (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <span
                        onClick={(e) => { e.stopPropagation(); openCustomer(row.original); }}
                        style={{ color: '#1A8CFF', cursor: 'pointer', fontWeight: 500, fontSize: '0.8rem' }}
                    >
                        View
                    </span>
                    {/* Opens WhatsApp on their number with nothing written — whoever
                        presses it is the one talking. Hidden when there is no number. */}
                    {row.original.phoneNumber ? (
                        <Tooltip title={`WhatsApp ${formatPhoneNumber(row.original.phoneNumber)}`}>
                            <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); openWhatsApp(row.original.phoneNumber, ''); }}
                                sx={{ p: 0.25, color: '#128C7E' }}
                            >
                                <WhatsAppIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    ) : null}
                </Box>
            ),
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [base]);

    const csvConfig = mkConfig({ filename: 'Customer_Data_Export', fieldSeparator: ',', decimalSeparator: '.', useKeysAsHeaders: true });

    const handleExportData = () => {
        const exportData = customers.map(c => ({
            'Customer Name': c.customerName,
            'Phone': c.phoneNumber,
            'Birthday': c.birthday ?? '',
            'IC': c.ic ?? '',
            'kWp': c.energy || '',
            'Final Price': c.finalPrice || '',
            'Deposit Date': c.depositOn ? convertDateTimeToDateString(c.depositOn) : '',
            'Sales': c.salesCount,
            'Email': c.email ?? '',
            'Address': c.address ?? '',
            'Sales Agent': c.salesAgent,
            'Co-Agent (Offline)': c.coAgent,
        }));
        const csv = generateCsv(csvConfig)(exportData);
        download(csvConfig)(csv);
    };

    const table = useMaterialReactTable({
        // Excel-style column filters: a search box over a tick-box list of the values
        // actually in that column, for every column at once. A column with nearly one
        // value per row falls back to the plain box on its own.
        // (user 2026-10-06: "for all table maybe yes. go ahead with this method")
        ...facetFilterOptions,
        columns, data: customers,
        enableColumnActions: false, enableColumnDragging: false,
        enableDensityToggle: false, enableFullScreenToggle: false, enableHiding: false,
        enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
        initialState: { density: 'compact', showGlobalFilter: true },
        state: { globalFilter: ts.globalFilter, sorting: ts.sorting, pagination: ts.pagination, columnFilters: ts.columnFilters },
        onGlobalFilterChange: ts.setGlobalFilter,
        onSortingChange: ts.setSorting,
        onPaginationChange: ts.setPagination,
        onColumnFiltersChange: ts.setColumnFilters,
        muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
        muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif", padding: '8px 10px' } },
        muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: "'Poppins', sans-serif", padding: '6px 10px' } },
        muiTableBodyRowProps: ({ row }) => ({
            onClick: () => openCustomer(row.original),
            sx: { cursor: 'pointer', '&:hover': { backgroundColor: '#F0F7FF', transition: 'background-color 0.12s' } },
        }),
        muiTopToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', minHeight: '48px' } },
        muiBottomToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' } },
        muiSearchTextFieldProps: { placeholder: 'Search customers...', sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#F8FAFC', fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif" } } },
        muiPaginationProps: { shape: 'rounded', showRowsPerPage: true, rowsPerPageOptions: [10, 25, 50, 100] },
        renderTopToolbarCustomActions: () => (
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button onClick={handleExportData} style={{ border: '1px solid #E2E8F0', background: 'white', borderRadius: '7px', padding: '6px 14px', fontSize: '0.775rem', fontWeight: 500, color: '#374151', cursor: 'pointer', fontFamily: "'Poppins', sans-serif", display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <DownloadIcon size={14} /> Export All
                </button>
            </Box>
        ),
    });

    if (loading) return <div>Loading...</div>;

    return (
        <div>
            <Box sx={{ mb: 2 }}>
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>All Customer</h2>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: "'Poppins', sans-serif" }}>
                    {isAdmin ? 'View and manage all customers' : 'View your customers'}
                </p>
            </Box>
            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <MaterialReactTable table={table} />
            </div>
        </div>
    );
}
